import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import './AdminImageUploader.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api')+ '/api';

function AdminImageUploader() {
  const [form, setForm] = useState({
    category: 'founder',
    subCategory: '',
    alt: '',
    order: '0',
    isActive: true,
    contactInfo: { name: '', position: '' }
  });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [images, setImages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    'founder', 'executiveDirector', 'director', 'generalManagerHead',
    'generalManager', 'serviceCenter', 'gac', 'futureShowroom', 'ridara', 'customer',
      'Finance',
      'Staff',
      'HeadOffice'
  ];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setListLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const res = await axios.get(`${API_BASE}/images/all`, {
        headers: {
          'Accept': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
      });

      setImages(res.data.images || []);
    } catch (err) {
      console.error('Failed to load images:', err);
      const errorMsg = err.response?.status === 401 
        ? '401 Unauthorized – check your REACT_APP_VEHICLES_API_KEY in .env'
        : (err.response?.data?.message || 'Could not load images');
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setListLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('contactInfo.')) {
      const field = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        contactInfo: { ...prev.contactInfo, [field]: value },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setForm({
      category: 'founder',
      subCategory: '',
      alt: '',
      order: '0',
      isActive: true,
      contactInfo: { name: '', position: '' }
    });
    setImageFile(null);
    setPreview(null);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let imageUrl = form.imageUrl;
      let oldImageUrl = null;

      // If editing and uploading a new image, save the old URL for deletion
      if (editingId && imageFile && form.imageUrl) {
        oldImageUrl = form.imageUrl;
      }

      if (imageFile) {
        const categoryFolder = form.category === 'founder' ? 'company/founder' : `company/${form.category}`;
        const fileName = `${Date.now()}_${imageFile.name}`;
        const storageRef = ref(storage, `${categoryFolder}/${fileName}`);

        const snapshot = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      const payload = {
        imageUrl,
        category: form.category,
        subCategory: form.subCategory || null,
        alt: form.alt,
        order: parseInt(form.order) || 0,
        isActive: form.isActive,
        contactInfo: form.contactInfo.name || form.contactInfo.position 
          ? form.contactInfo 
          : undefined
      };

      if (editingId) {
        await axios.put(`${API_BASE}/images/${editingId}`, payload, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || ''
          }
        });

        // Delete old image from Firebase Storage if a new one was uploaded
        if (oldImageUrl && imageFile) {
          try {
            const oldImageRef = ref(storage, oldImageUrl);
            await deleteObject(oldImageRef);
            console.log('Old image deleted from Firebase Storage');
          } catch (deleteErr) {
            console.error('Error deleting old image from Firebase:', deleteErr);
            // Don't fail the update if deletion fails
          }
        }

        setMessage({ type: 'success', text: 'Image updated successfully!' });
      } else {
        if (!imageFile) {
          setMessage({ type: 'error', text: 'Please select an image file' });
          setLoading(false);
          return;
        }
        
        await axios.post(`${API_BASE}/images`, payload, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || ''
          }
        });
        setMessage({ type: 'success', text: 'Image uploaded successfully!' });
      }

      resetForm();
      fetchImages();
    } catch (err) {
      console.error('Submit error:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || err.message || 'Operation failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (img) => {
    setForm({
      category: img.category,
      subCategory: img.subCategory || '',
      alt: img.alt || '',
      order: String(img.order || 0),
      isActive: img.isActive,
      contactInfo: img.contactInfo || { name: '', position: '' },
      imageUrl: img.imageUrl
    });
    setPreview(img.imageUrl);
    setEditingId(img._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (img) => {
    if (!window.confirm('Delete this image permanently? This will remove it from both storage and database.')) {
      return;
    }

    try {
      // Step 1: Delete from Firebase Storage
      if (img.imageUrl) {
        try {
          const imageRef = ref(storage, img.imageUrl);
          await deleteObject(imageRef);
          console.log('Image deleted from Firebase Storage');
        } catch (storageErr) {
          // If file doesn't exist in storage, log but continue
          if (storageErr.code !== 'storage/object-not-found') {
            console.error('Error deleting from Firebase Storage:', storageErr);
          }
        }
      }

      // Step 2: Delete from MongoDB
      await axios.delete(`${API_BASE}/images/${img._id}`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
      });

      setMessage({ type: 'success', text: 'Image deleted successfully from both storage and database' });
      fetchImages();
    } catch (err) {
      console.error('Delete failed:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to delete image' });
    }
  };

  const filteredImages = images
    .filter(img => filterCategory === 'all' || img.category === filterCategory)
    .filter(img => 
      searchTerm === '' || 
      img.alt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      img.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      img.contactInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1 className="admin-title">
          <span className="title-icon">🖼️</span>
          Company Image Manager
        </h1>
        <p className="admin-subtitle">Upload and manage company background images</p>
      </div>

      {message.text && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <span className="alert-icon">
            {message.type === 'success' ? '✓' : '⚠'}
          </span>
          {message.text}
          <button 
            onClick={() => setMessage({ type: '', text: '' })}
            className="alert-close"
          >
            ×
          </button>
        </div>
      )}

      <div className="form-card">
        <div className="form-header">
          <h2 className="form-title">
            {editingId ? '✏️ Edit Image' : '➕ Add New Image'}
          </h2>
          {editingId && (
            <button onClick={resetForm} className="cancel-button">
              Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Category <span className="required">*</span>
              </label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                required
                className="form-select"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.replace(/([A-Z])/g, ' $1').trim()}
                  </option>
                ))}
              </select>
            </div>

            {form.category === 'generalManager' && (
              <div className="form-group">
                <label className="form-label">Sub-category</label>
                <select
                  name="subCategory"
                  value={form.subCategory}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">None</option>
                  <option value="service">Service</option>
                  <option value="admin">Admin</option>
                  <option value="hiring">Hiring</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Alt Text</label>
              <input
                type="text"
                name="alt"
                value={form.alt}
                onChange={handleChange}
                placeholder="e.g., Founder at company event"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                name="order"
                value={form.order}
                onChange={handleChange}
                min="0"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Image {!editingId && <span className="required">*</span>}
            </label>
            <div className="upload-area">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFile}
                required={!editingId}
                className="file-input"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="upload-label">
                <div className="upload-icon">📁</div>
                <div className="upload-text">
                  {imageFile ? imageFile.name : 'Click to upload or drag and drop'}
                </div>
                <div className="upload-subtext">JPG, PNG or WEBP (max. 5MB)</div>
              </label>
            </div>
            {preview && (
              <div className="preview-container">
                <img
                  src={preview}
                  alt="Preview"
                  className="preview-image"
                />
              </div>
            )}
          </div>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                className="form-checkbox"
              />
              <span>Active (visible on website)</span>
            </label>
          </div>

          {(form.category === 'executiveDirector' ||
            form.category === 'director' ||
            form.category === 'generalManagerHead' ||
            form.category === 'generalManager') && (
            <div className="contact-section">
              <h3 className="contact-title">Contact Information (Optional)</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    placeholder="John Doe"
                    name="contactInfo.name"
                    value={form.contactInfo.name}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Position / Title</label>
                  <input
                    placeholder="Executive Director"
                    name="contactInfo.position"
                    value={form.contactInfo.position}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`submit-button ${loading ? 'disabled' : ''}`}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                {editingId ? 'Updating...' : 'Uploading...'}
              </>
            ) : (
              editingId ? '💾 Update Image' : '⬆️ Upload Image'
            )}
          </button>
        </form>
      </div>

      <div className="list-section">
        <div className="list-header">
          <h2 className="list-title">
            Existing Images ({filteredImages.length})
          </h2>
          
          <div className="controls">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search images..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.replace(/([A-Z])/g, ' $1').trim()}
                </option>
              ))}
            </select>

            <div className="view-toggle">
              <button
                onClick={() => setViewMode('grid')}
                className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
              >
                ⊞
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              >
                ☰
              </button>
            </div>
          </div>
        </div>

        {listLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading images...</p>
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p className="empty-text">
              {searchTerm || filterCategory !== 'all' 
                ? 'No images match your filters' 
                : 'No images uploaded yet'}
            </p>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'images-grid' : 'images-list'}>
            {filteredImages.map((img) => (
              <div
                key={img._id}
                className={viewMode === 'grid' ? 'image-card' : 'image-list-item'}
              >
                <div className="image-wrapper">
                  <img
                    src={img.imageUrl}
                    alt={img.alt || img.category}
                    className="card-image"
                  />
                  {!img.isActive && (
                    <div className="inactive-badge">Inactive</div>
                  )}
                </div>
                
                <div className="card-content">
                  <div className="card-header">
                    <h3 className="card-title">
                      {img.category.replace(/([A-Z])/g, ' $1').trim()}
                    </h3>
                    <span className="order-badge">#{img.order}</span>
                  </div>
                  
                  {img.subCategory && (
                    <div className="sub-category">{img.subCategory}</div>
                  )}
                  
                  {img.alt && (
                    <p className="alt-text">{img.alt}</p>
                  )}
                  
                  {img.contactInfo && (img.contactInfo.name || img.contactInfo.position) && (
                    <div className="contact-info">
                      {img.contactInfo.name && (
                        <div className="contact-name">👤 {img.contactInfo.name}</div>
                      )}
                      {img.contactInfo.position && (
                        <div className="contact-position">💼 {img.contactInfo.position}</div>
                      )}
                    </div>
                  )}
                  
                  <div className="card-actions">
                    <button
                      onClick={() => handleEdit(img)}
                      className="edit-button"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(img)}
                      className="delete-button"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminImageUploader;